import { useMemo } from 'react';
import { type SupportedLocale } from '@zep-us/i18n';

import type { CategoryTreeBuildDto } from '@/api/gen/_model/categoryTreeBuildDto';
import type { SplitCategoryTreeResponse } from '@/api/gen/_model/splitCategoryTreeResponse';
import { useLocale } from '@/hooks';
import {
  type PublicCategoryState,
  usePublicCategoryFilter,
} from '@/module/public/hooks/usePublicCategoryFilter';
import { groupBySubjectNodes } from '@/shared/category/subject.util';
import {
  type CategoryTree,
  type GroupedSubjects,
} from '@/shared/category/type';
import { useCachedCategoryInfo } from '@/shared/category/useFetchCategoryInfo';
import {
  AllSchoolType,
  isShowSemester,
  makeSemesterNodes,
  PublisherLabelMap,
} from '@/shared/category/util';
import { type PublisherInfo, type SchoolType } from '@/views/build/type';
import { getGradeNumber, isAllGrades } from '@/views/build/util';

const LOCAL_FALLBACK_PUBLISHERS: PublisherInfo[] = Object.entries(
  PublisherLabelMap,
)
  .filter(([value]) => value !== 'OTHER')
  .map(([value, label]) => ({
    value,
    label,
    hideGrades: [],
    showSchoolTypes: ['primary', 'middle', 'high'],
  }));

/**
 *  클라이언트에서 카테고리 트리를 가져오는 훅입니다.
 * */
export const usePublicCategories = (
  stateOverride?: Partial<PublicCategoryState>,
): {
  data?: SplitCategoryTreeResponse;
  schoolTypes: FilterNode[];
  grades: FilterNode[];
  semesters: FilterNode[];
  publishers: PublisherInfo[];
  subjectGroups: GroupedSubjects;
} => {
  const { locale } = useLocale();
  const {
    selectedSchoolTypeName: publicSelectedSchoolTypeName,
    selectedGrades: publicSelectedGrades,
    selectedSubject: publicSelectedSubject,
    selectedSemesters: publicSelectedSemesters,
  } = usePublicCategoryFilter();

  const _selectedSchoolTypeName =
    stateOverride?.schoolType ?? publicSelectedSchoolTypeName;
  const selectedGrades = stateOverride?.grades ?? publicSelectedGrades;
  const selectedSubject = stateOverride?.subject ?? publicSelectedSubject;
  const selectedSemesters = stateOverride?.semesters ?? publicSelectedSemesters;

  const selectedSchoolTypeName =
    _selectedSchoolTypeName === AllSchoolType.name
      ? ''
      : _selectedSchoolTypeName;

  const { publishersData, categoryTreeData, countryCode } =
    useCachedCategoryInfo();

  const selectedSchoolType = selectedSchoolTypeName?.toLowerCase() as
    | SchoolType
    | '';
  const selectedGradeNumbers = selectedGrades
    .map(getGradeNumber)
    .filter(grade => grade > 0 && !isAllGrades(grade));
  const publisherGradeOffset =
    selectedSchoolType === 'middle' ? 6 : selectedSchoolType === 'high' ? 9 : 0;
  const publisherGradeNumbers = selectedGradeNumbers.map(
    grade => grade + publisherGradeOffset,
  );

  const publisherSource =
    publishersData?.length || process.env.NEXT_PUBLIC_STAGE !== 'LOC'
      ? publishersData ?? []
      : LOCAL_FALLBACK_PUBLISHERS;

  const publishers = publisherSource
    .filter(d => d.value !== 'OTHER')
    .filter(data =>
      data.showSchoolTypes.includes(selectedSchoolType as SchoolType),
    )
    .filter(
      data =>
        publisherGradeNumbers.length === 0 ||
        publisherGradeNumbers.some(grade => !data.hideGrades.includes(grade)),
    );

  const schoolTypes = useMemo(() => {
    return categoryTreeData?.schoolInfoTree.children
      ? [AllSchoolType].concat(
          categoryTreeData.schoolInfoTree.children.map(c => ({
            ...c,
            value: c.name,
          })),
        )
      : [];
  }, [categoryTreeData?.schoolInfoTree.children]);

  const grades = (
    schoolTypes
      .find(d => d.name.toUpperCase() === selectedSchoolTypeName.toUpperCase())
      ?.children.filter(d => d.name !== 'COMMON') || []
  ).map(c => ({
    ...c,
    value: c.categoryGroupId,
  }));

  const getSemesters = (): FilterNode[] => {
    if (!isShowSemester(selectedSchoolTypeName, countryCode)) return [];
    return makeSemesterNodes();
  };

  const getTreeIds = useMemo(() => {
    return {
      schoolType:
        schoolTypes?.find(d => d.name === selectedSchoolTypeName)
          ?.categoryTreeId ?? '',
      grades: grades
        .filter(g => selectedGrades.includes(g.value))
        .map(d => d.categoryTreeId),
      semester: selectedSemesters,
      subject: selectedSubject,
    };
  }, [
    schoolTypes,
    grades,
    selectedSemesters,
    selectedSubject,
    selectedSchoolTypeName,
    selectedGrades,
  ]);

  const subjectGroups = useMemo(() => {
    return groupByCategoryGroupId(
      // 일단 학교로 1차 필터
      categoryTreeData?.subjectTree.filter(d =>
        !selectedSchoolTypeName
          ? true
          : d.ancestors.includes(getTreeIds.schoolType),
      ) ?? [],
      // 학년으로 추가로 필터르합니다.
      [...getTreeIds.grades].filter(Boolean),
      locale,
    );
  }, [
    categoryTreeData?.subjectTree,
    getTreeIds.grades,
    getTreeIds.schoolType,
    locale,
    selectedSchoolTypeName,
  ]);

  return {
    data: categoryTreeData,
    schoolTypes,
    grades,
    publishers,
    semesters: getSemesters(),
    subjectGroups,
  };
};

function groupByCategoryGroupId(
  subjectCategories: CategoryTreeBuildDto[],
  ancestors: string[],
  locale: SupportedLocale,
) {
  const filteredCategories = subjectCategories.filter(d => {
    return ancestors.length === 0
      ? true
      : d.ancestors.some(d => ancestors.includes(d));
  });

  return groupBySubjectNodes(filteredCategories, locale);
}

export type FilterNode = CategoryTree & { value: string };
